// ===========================================
// Similar Prompts Component - Semantic Similarity Display
// ===========================================
// Shows prompts that are semantically similar to the current prompt
// Uses VoyageAI embeddings for high-quality similarity matching

import React, { useState, useEffect } from 'react';
import { 
  Sparkles, 
  Loader,
  Copy,
  Check
} from 'lucide-react';
import { promptsAPI } from '../services/api';
import './SimilarPrompts.scss';

const SimilarPrompts = ({ promptId, onSelectPrompt }) => {
  // ===========================================
  // State
  // ===========================================
  const [similar, setSimilar] = useState([]);
  const [loading, setLoading] = useState(true);
  // Error state - setError used for state management, value intentionally unused in UI
  const [, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  // ===========================================
  // Fetch Similar Prompts via Embeddings
  // ===========================================
  useEffect(() => {
    const fetchSimilar = async () => {
      if (!promptId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await promptsAPI.getSimilar(promptId, { 
          limit: 5, 
          minScore: 0.4  // Only show prompts with decent similarity
        });
        
        if (response.data.success) {
          setSimilar(response.data.results || []);
        }
      } catch (err) {
        // Embeddings may not be available or no similar prompts found
        // This is not a critical error, just show empty state
        console.debug('Similar prompts not available:', err.message);
        setError(null);  // Don't show error to user
        setSimilar([]);
      }
      
      setLoading(false);
    };

    fetchSimilar();
  }, [promptId]);

  // ===========================================
  // Copy Handler
  // ===========================================
  const handleCopy = async (e, prompt) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(prompt.content);
      setCopiedId(prompt._id);
      
      // Record the copy for analytics
      await promptsAPI.recordCopy(prompt._id);
      
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // ===========================================
  // Render
  // ===========================================
  
  // Don't show if loading or no similar prompts
  if (loading) {
    return (
      <div className="similar-prompts similar-prompts--loading">
        <Loader className="animate-spin" size={14} />
        <span>Finding similar prompts...</span>
      </div>
    );
  }

  // Don't render anything if no similar prompts found
  if (!similar || similar.length === 0) {
    return null;
  }

  return (
    <div className="similar-prompts">
      <div className="similar-prompts__header">
        <Sparkles size={14} />
        <span>Similar Prompts</span>
        <span className="similar-prompts__badge">{similar.length}</span>
      </div>
      
      <div className="similar-prompts__list">
        {similar.map((item) => (
          <div 
            key={item.prompt._id} 
            className="similar-prompts__item"
            onClick={() => onSelectPrompt?.(item.prompt)}
          >
            <div className="similar-prompts__item-header">
              <span className="similar-prompts__item-title">
                {item.prompt.title}
              </span>
              <span className="similar-prompts__item-score" title="Similarity score">
                {Math.round(item.score * 100)}%
              </span>
            </div>
            
            {item.prompt.description && (
              <p className="similar-prompts__item-description">
                {item.prompt.description.length > 80 
                  ? item.prompt.description.substring(0, 80) + '...'
                  : item.prompt.description
                }
              </p>
            )}
            
            <div className="similar-prompts__item-actions">
              <button
                className={`similar-prompts__copy-btn ${copiedId === item.prompt._id ? 'copied' : ''}`}
                onClick={(e) => handleCopy(e, item.prompt)}
                title="Copy prompt"
              >
                {copiedId === item.prompt._id ? <Check size={12} /> : <Copy size={12} />}
              </button>
              
              {item.prompt.tags?.slice(0, 3).map(tag => (
                <span key={tag} className="similar-prompts__tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SimilarPrompts;
